/**
 * Format a duration in seconds as a zero-padded `MM:SS` string (e.g. 94 → "01:34").
 * Used by countdown timers (resend-code cooldowns). Negative values clamp to "00:00".
 */
export const formatMMSS = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60)
    .toString()
    .padStart(2, '0');
  const s = (safe % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

/**
 * Translation function type for time ago strings
 */
type TimeAgoTranslator = (
  key:
    | 'secAgo'
    | 'minAgo'
    | 'hourAgo'
    | 'hoursAgo'
    | 'dayAgo'
    | 'daysAgo'
    | 'secShort'
    | 'minShort'
    | 'hourShort'
    | 'dayShort',
  params: { count: number },
) => string;

/**
 * Format timestamp to human readable "time ago" string
 * @param timestamp - Date object to format (should be in UTC)
 * @param short - Whether to return short format (5 s) or long format (5 sec ago)
 * @param nowOverride - Optional current time (UTC) to avoid SSR/client drift
 * @param t - Optional translation function for i18n support (from useTranslations('common.timeAgo'))
 * @returns Formatted string like "5 sec ago", "3 min ago", etc. or "5 s", "3 m", etc.
 */
export const formatTimeAgo = (
  timestamp: Date,
  short: boolean = false,
  nowOverride?: Date,
  t?: TimeAgoTranslator,
): string => {
  // Use UTC time for calculation
  const now = nowOverride ?? new Date();
  // getTime() returns milliseconds since epoch in UTC, so difference is correct
  const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000);

  if (diffInSeconds < 60) {
    if (t) {
      return short
        ? t('secShort', { count: diffInSeconds })
        : t('secAgo', { count: diffInSeconds });
    }
    return short ? `${diffInSeconds}s` : `${diffInSeconds} sec ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    if (t) {
      return short ? t('minShort', { count: minutes }) : t('minAgo', { count: minutes });
    }
    return short ? `${minutes}m` : `${minutes} min ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    if (t) {
      return short
        ? t('hourShort', { count: hours })
        : hours > 1
          ? t('hoursAgo', { count: hours })
          : t('hourAgo', { count: hours });
    }
    return short ? `${hours}h` : `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    if (t) {
      return short
        ? t('dayShort', { count: days })
        : days > 1
          ? t('daysAgo', { count: days })
          : t('dayAgo', { count: days });
    }
    return short ? `${days}d` : `${days} day${days > 1 ? 's' : ''} ago`;
  }
};
